/** @jsxImportSource theme-ui */

import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Flex, Switch, Text, Textarea } from 'theme-ui'
import { CopyButton } from '../components/copy-button'
import { CheckMarkIcon } from '../components/icons/checkmark'
import { SwapIcon } from '../components/icons/swap'
import { Mudapin, parseMudapins, tokenizeMudapins } from '../lib/mudapin-parser'
import { useStoredState } from '../lib/use-stored-state'

const useMudapinsApi = (): Mudapin[] => {
  const [pins, setPins] = useState<Mudapin[]>([])
  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    fetch('http://localhost:3000/mudapins', { signal })
      .then((res) => res.json())
      .then((res) => {
        console.log(res)
        setPins(res)
      })
    return () => controller.abort()
  }, [])
  return pins
}
const useMudapins = (): Mudapin[] => {
  const allMudapins = useStaticQuery(graphql`
    query GetAllMudapins {
      allMudapinsJson {
        nodes {
          name
          series
          jsonId
          localImage {
            childImageSharp {
              gatsbyImageData(
                sizes: "128px"
                placeholder: NONE
                layout: CONSTRAINED
                outputPixelDensities: 1
                width: 128
                height: 128
              )
            }
          }
        }
      }
    }
  `)

  const mapped = useMemo(
    () =>
      allMudapins.allMudapinsJson.nodes.map((n: any) => ({
        id: n.jsonId,
        name: n.name,
        series: n.series,
        image: getImage(n.localImage),
      })),
    [allMudapins],
  )

  return mapped
}

const parseMyMudapins = (input: string): Set<string> => {
  return new Set(tokenizeMudapins(input))
}

export default () => {
  const allMudapins = useMudapins()

  const [mudapinsValue, setMudapinsValue] = useStoredState(
    'mudapins-mudapins-value',
    '',
  )

  const [myMudapinsValue, setMyMudapinsValue] = useStoredState(
    'mudapins-my-mudapins-value',
    '',
  )

  const [hideAlreadyOwned, setHideAlreadyOwned] = useStoredState(
    'mudapins-hide-already-owned',
    false,
  )
  const [showCopyableList, setShowCopyableList] = useStoredState(
    'mudapins-show-copyable-list',
    false,
  )

  const mudapins = useMemo(() => {
    return parseMudapins(allMudapins, mudapinsValue)
  }, [allMudapins, mudapinsValue])

  const myPins = useMemo<Set<string>>(() => {
    return parseMyMudapins(myMudapinsValue)
  }, [myMudapinsValue])

  return (
    <>
      <Text sx={{ mb: 2 }}>
        Paste a list of mudapins. You can get your mudapins with the following
        command: <CopyableCode code={'$mps'} />
      </Text>

      <Textarea
        value={mudapinsValue}
        onInput={(ev) => {
          const value = ev.currentTarget.value
          setMudapinsValue(value)
        }}
      />

      <Text sx={{ mb: 2, mt: 3 }}>
        If you want, you can also paste your owns pins below, and get a{' '}
        <CheckMarkIcon sx={{ size: 4, verticalAlign: 'middle' }} /> for any pins
        that you already have.
      </Text>

      <Textarea
        value={myMudapinsValue}
        onInput={(ev) => {
          const value = ev.currentTarget.value
          setMyMudapinsValue(value)
        }}
      />

      <Flex
        sx={{
          flexDirection: ['column', 'row'],
          alignItems: ['stretch', 'center'],
          gap: 4,
          lineHeight: '22px',
          mb: 4,
        }}
      >
        <Button
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 5,
            flexShrink: 0,
          }}
          onClick={() => {
            setMudapinsValue(myMudapinsValue)
            setMyMudapinsValue(mudapinsValue)
          }}
        >
          <SwapIcon sx={{ size: 4, mr: 2 }} />
          Swap
        </Button>

        <Switch
          label="Hide already owned pins?"
          checked={hideAlreadyOwned}
          onChange={(ev) => setHideAlreadyOwned(ev.currentTarget.checked)}
        />

        <Switch
          label="Show copyable list?"
          checked={showCopyableList}
          onChange={(ev) => setShowCopyableList(ev.currentTarget.checked)}
        />
      </Flex>
      {showCopyableList ? <CopyableList items={mudapins} /> : null}

      <Box sx={{ p: 0, my: 4, display: 'flex', flexDirection: 'column' }}>
        <Text>Total number of pins: {mudapins.length}</Text>
      </Box>

      <Box
        as="ul"
        sx={{
          p: 0,
          m: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {mudapins
          .filter((mp) => !hideAlreadyOwned || !myPins.has(mp.id))
          .map((item) => {
            return (
              <MudapinComponent
                mudapin={item}
                key={item.id}
                owned={myPins.has(item.id)}
              />
            )
          })}
      </Box>
    </>
  )
}

const CopyableCode: React.FC<{ code: string }> = ({ code }) => {
  return (
    <code
      onClick={() => navigator.clipboard.writeText(code)}
      style={{ userSelect: 'all' }}
    >
      {code}
    </code>
  )
}

const MudapinComponent: React.FC<{ mudapin: Mudapin; owned: boolean }> =
  React.memo(({ mudapin, owned }) => {
    return (
      <Flex
        as="li"
        sx={{
          paddingBlock: 2,
          paddingInline: [3, 4],
          listStyle: 'none',
          alignItems: 'center',
          position: 'relative',
          marginBlockEnd: 4,
          backgroundColor: 'muted',
          borderRadius: 2,
          contain: 'content', // Required for the border radius to clip properly
          width: '100%',
          maxWidth: 13,
        }}
      >
        <GatsbyImage
          sx={{ mr: 4, flexShrink: 0, width: [8, 9] }}
          image={mudapin.image}
          //src={`http://localhost:3000${mudapin.image}`}
          alt={mudapin.id}
        />
        <Text>
          <strong>{mudapin.series}</strong>
          <br />
          {mudapin.name}
          <br />
          <code>{mudapin.id}</code>
        </Text>
        <CopyButton
          sx={{
            ml: 'auto',
            mr: 6,
            background: 'transparent',
            borderColor: 'primary',
            borderStyle: 'solid',
            borderWidth: 1,
            color: 'primary',
            fill: 'primary',
          }}
          text={`${mudapin.id} - ${mudapin.name} from ${mudapin.series}`}
        />
        {owned && <CheckMarkIcon sx={{ position: 'absolute', right: 3 }} />}
      </Flex>
    )
  })

const CopyableList: React.FC<{ items: Mudapin[] }> = ({ items }) => {
  const copyableList = items.map((mp) => mp.id).join(' $ ')
  return (
    <Box>
      <CopyButton sx={{ mb: 2 }} text={copyableList}>
        Copy list to clipboard
      </CopyButton>
      <Text>{copyableList}</Text>
    </Box>
  )
}
